import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { api, unwrap } from '~/lib/api'
import type { Status } from './labels'

// Keys start with the domain and carry team + season, so SSE invalidations (['training']) and team or
// season switches refetch exactly this data (ARCHITECTURE §6.2).
export const trainingKeys = {
  all: ['training'] as const,
  scope: (teamId: string, season: string) => ['training', teamId, season] as const,
}

export const sessionsQuery = (teamId: string, season: string, from: string, to: string) =>
  queryOptions({
    queryKey: [...trainingKeys.scope(teamId, season), 'sessions', from, to],
    queryFn: () => unwrap(api.teams[':teamId'].sessions.$get({ param: { teamId }, query: { from, to } })),
    enabled: !!teamId,
  })

export type SessionsData = Awaited<ReturnType<NonNullable<ReturnType<typeof sessionsQuery>['queryFn']>>>

export const sessionQuery = (teamId: string, season: string, id: string) =>
  queryOptions({
    queryKey: [...trainingKeys.scope(teamId, season), 'session', id],
    queryFn: () => unwrap(api.sessions[':id'].$get({ param: { id } })),
  })

export type SessionDetail = Awaited<ReturnType<NonNullable<ReturnType<typeof sessionQuery>['queryFn']>>>
export type RosterRow = SessionDetail['roster'][number]

export type Range = { from?: string; to?: string; season?: string }

export const attendanceQuery = (teamId: string, season: string, range: Range) =>
  queryOptions({
    queryKey: [...trainingKeys.scope(teamId, season), 'attendance', range.from ?? '', range.to ?? ''],
    queryFn: () => unwrap(api.teams[':teamId'].attendance.$get({ param: { teamId }, query: { ...range, season } })),
    enabled: !!teamId,
  })

export type AttendanceData = Awaited<ReturnType<NonNullable<ReturnType<typeof attendanceQuery>['queryFn']>>>

export const attendanceSessionsQuery = (teamId: string, season: string, range: Range) =>
  queryOptions({
    queryKey: [...trainingKeys.scope(teamId, season), 'attendance-sessions', range.from ?? '', range.to ?? ''],
    queryFn: () =>
      unwrap(api.teams[':teamId'].attendance.sessions.$get({ param: { teamId }, query: { ...range, season } })),
    enabled: !!teamId,
  })

export const personAttendanceQuery = (teamId: string, season: string, personId: string, range: Range) =>
  queryOptions({
    queryKey: [...trainingKeys.scope(teamId, season), 'attendance-person', personId, range.from ?? '', range.to ?? ''],
    queryFn: () =>
      unwrap(
        api.teams[':teamId'].attendance.people[':personId'].$get({ param: { teamId, personId }, query: { ...range, season } }),
      ),
  })

export const heatmapQuery = (teamId: string, season: string, last: number) =>
  queryOptions({
    queryKey: [...trainingKeys.scope(teamId, season), 'heatmap', last],
    queryFn: () =>
      unwrap(api.teams[':teamId'].attendance.heatmap.$get({ param: { teamId }, query: { last: String(last), season } })),
    enabled: !!teamId,
  })

/** Refetch everything of the domain after a write (SSE does the same for other tabs and people). */
function useRefresh() {
  const qc = useQueryClient()
  return () => Promise.all([qc.invalidateQueries({ queryKey: trainingKeys.all }), qc.invalidateQueries({ queryKey: ['calendar'] })])
}

export type SessionInput = {
  kind?: 'training' | 'meeting' | 'club_event'
  title: string
  date: string
  time: string
  endTime?: string | null
  location?: string | null
  focus?: string | null
  description?: string | null
}

export function useCreateSession(teamId: string) {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (body: SessionInput) => unwrap(api.teams[':teamId'].sessions.$post({ param: { teamId }, json: body })),
    onSuccess: refresh,
  })
}

/** Club-wide events and meetings (calendar). */
export function useCreateClubSession() {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (body: SessionInput & { teamId?: string | null }) => unwrap(api.sessions.$post({ json: body })),
    onSuccess: refresh,
  })
}

export type SeriesInput = {
  title: string
  weekdays: number[]
  time: string
  endTime?: string | null
  from: string
  until: string
  location?: string | null
  focus?: string | null
}

export function useCreateSeries(teamId: string) {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (body: SeriesInput) =>
      unwrap(api.teams[':teamId'].sessions.series.$post({ param: { teamId }, json: body })),
    onSuccess: refresh,
  })
}

export type SessionPatch = Partial<Omit<SessionInput, 'kind'>> & { scope?: 'this' | 'following' }

export function useUpdateSession(id: string) {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (body: SessionPatch) => unwrap(api.sessions[':id'].$patch({ param: { id }, json: body })),
    onSuccess: refresh,
  })
}

export function useCancelSession(id: string) {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (body: { reason?: string | null; scope?: 'this' | 'following' }) =>
      unwrap(api.sessions[':id'].cancel.$post({ param: { id }, json: body })),
    onSuccess: refresh,
  })
}

export function useReinstateSession(id: string) {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: () => unwrap(api.sessions[':id'].reinstate.$post({ param: { id } })),
    onSuccess: refresh,
  })
}

export function useDeleteSession(id: string) {
  const qc = useQueryClient()
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (scope: 'this' | 'following') => unwrap(api.sessions[':id'].$delete({ param: { id }, query: { scope } })),
    // Drop the detail first: refetching a deleted session would only produce a 404.
    onSuccess: () => {
      qc.removeQueries({ predicate: (q) => q.queryKey.includes('session') && q.queryKey.includes(id) })
      return refresh()
    },
  })
}

type Mark = { personId: string; status: Status | null }

/** Pause after the last tap on a row before its mark is sent: cycling through statuses sends once. */
const MARK_DEBOUNCE_MS = 400

/**
 * Pitchside autosave. The detail cache changes at once (no spinner per tap); per row only the latest
 * status goes out, after a short pause; all requests of a session run one after another in tap order
 * (mutation scope), so the last tap is what gets stored — parallel PUTs used to finish in any order.
 * Refetches wait until nothing is pending, so an early response cannot flip later taps back.
 */
export function useMarks(teamId: string, season: string, sessionId: string) {
  const qc = useQueryClient()
  const key = sessionQuery(teamId, season, sessionId).queryKey
  const mutationKey = ['training', 'marks', sessionId]
  const timers = useRef(new Map<string, { timer: ReturnType<typeof setTimeout>; send: () => void; base: Status | null }>())

  const apply = (entries: Mark[]) => {
    void qc.cancelQueries({ queryKey: key })
    const by = new Map(entries.map((e) => [e.personId, e.status]))
    qc.setQueryData(key, (old) => {
      if (!old) return old
      const roster = old.roster.map((r) => (by.has(r.personId) ? { ...r, status: by.get(r.personId)!, source: 'coach' as const } : r))
      const counts = { present: 0, excused: 0, injured: 0, unexcused: 0, absent: 0, unknown: 0 }
      for (const r of roster) if (r.status) counts[r.status]++
      const total = Object.values(counts).reduce((a, b) => a + b, 0)
      const adj = counts.present + counts.unexcused + counts.absent
      return {
        ...old,
        roster,
        summary: {
          ...old.summary,
          ...counts,
          counts,
          sessions: total,
          rateTotal: total ? Math.round((counts.present / total) * 1000) / 10 : null,
          rateAdjusted: adj ? Math.round((counts.present / adj) * 1000) / 10 : null,
        },
      }
    })
  }

  const m = useMutation({
    mutationKey,
    scope: { id: `training-marks:${sessionId}` },
    mutationFn: async (entries: Mark[]): Promise<void> => {
      if (entries.length === 1)
        await unwrap(
          api.sessions[':id'].attendance[':personId'].$put({
            param: { id: sessionId, personId: entries[0]!.personId },
            json: { status: entries[0]!.status },
          }),
        )
      else await unwrap(api.sessions[':id'].attendance.$put({ param: { id: sessionId }, json: { entries } }))
    },
    onMutate: apply,
    // A snapshot rollback would also undo later queued taps; the server state is the truth.
    onError: () => qc.invalidateQueries({ queryKey: key }),
    onSettled: () =>
      // This mutation still counts as pending here.
      qc.isMutating({ mutationKey }) <= 1 && timers.current.size === 0
        ? qc.invalidateQueries({ queryKey: trainingKeys.all })
        : undefined,
  })

  // Leaving the page within the pause still saves the last tap.
  useEffect(() => {
    const pending = timers.current
    return () => {
      for (const { timer, send } of pending.values()) {
        clearTimeout(timer)
        send()
      }
      pending.clear()
    }
  }, [])

  /** One row. `onSaved` gets the status before this burst of taps (for undo). */
  function markOne(personId: string, status: Status | null, before: Status | null, onSaved?: (before: Status | null) => void) {
    const prev = timers.current.get(personId)
    if (prev) clearTimeout(prev.timer)
    const base = prev ? prev.base : before
    apply([{ personId, status }])
    const send = () => {
      timers.current.delete(personId)
      m.mutateAsync([{ personId, status }]).then(
        () => onSaved?.(base),
        () => undefined, // toasted by the query client
      )
    }
    timers.current.set(personId, { timer: setTimeout(send, MARK_DEBOUNCE_MS), send, base })
  }

  /** Several rows at once ("Alle anwesend", undo of it): no pause, same queue. */
  function markMany(entries: Mark[], onSaved?: () => void) {
    m.mutateAsync(entries).then(
      () => onSaved?.(),
      () => undefined,
    )
  }

  return { markOne, markMany, bulkPending: m.isPending && (m.variables?.length ?? 0) > 1 }
}

export function useAssess(teamId: string, season: string, sessionId: string) {
  const qc = useQueryClient()
  const key = sessionQuery(teamId, season, sessionId).queryKey
  return useMutation({
    mutationFn: (e: { personId: string; score: number | null; note?: string | null }) =>
      unwrap(api.sessions[':id'].assessments.$put({ param: { id: sessionId }, json: { entries: [e] } })),
    onMutate: async (e) => {
      await qc.cancelQueries({ queryKey: key })
      const prev = qc.getQueryData(key)
      qc.setQueryData(key, (old) =>
        old && {
          ...old,
          roster: old.roster.map((r) =>
            r.personId === e.personId ? { ...r, assessment: e.score ? { score: e.score, note: e.note ?? null } : null } : r,
          ),
        },
      )
      return { prev }
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev)
    },
  })
}
